'use client'

import { useState, useEffect } from 'react'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Check, ChevronsUpDown, Info } from 'lucide-react'
import type { Group } from '@/features/stm/lib/types'
import { ContactUserSelect } from '@/features/stm/components/forms/ContactUserSelect'
import { GROUP_LEADER_INFO } from '@/features/stm/constants/group-leader-tooltips'
import { getDisplayEmailValue, isNoEmailPlaceholderValue, NO_EMAIL_AVAILABLE_OPTION } from '@/features/volunteers/schemas/volunteers.schema'
import { cn } from '@/shared/lib/utils'

export type NewContactState = { name: string; email: string; phone: string }

type GroupLeaderCardProps = {
  group: Group
  contactUserId: number | null
  contactEmail: string
  contactPhone: string
  selectedContactEmail: string
  selectedContactPhone: string
  newContact: NewContactState
  onContactUserIdChange: (userId: number | null) => void
  onNewContactChange: (updater: (prev: NewContactState) => NewContactState) => void
  onSelectedContactEmailChange: (email: string) => void
  onSelectedContactPhoneChange: (phone: string) => void
  onSelectedContactInfoChange: (info: { userId: number; email?: string; phone?: string }) => void
  /** When true, all inputs are read-only */
  readOnly?: boolean
}

export function GroupLeaderCard({
  group,
  contactUserId,
  contactEmail,
  contactPhone,
  selectedContactEmail,
  selectedContactPhone,
  newContact,
  onNewContactChange,
  onContactUserIdChange,
  onSelectedContactEmailChange,
  onSelectedContactPhoneChange,
  onSelectedContactInfoChange,
  readOnly = false,
}: GroupLeaderCardProps) {
  const [selectedContactInfo, setSelectedContactInfo] = useState<{ email?: string; phone?: string } | null>(null)
  const [groupLeaderInfoOpen, setGroupLeaderInfoOpen] = useState(false)
  const [isEmailEditing, setIsEmailEditing] = useState(false)
  const [emailBeforeEdit, setEmailBeforeEdit] = useState('')
  const [isNoGroupLeaderCleared, setIsNoGroupLeaderCleared] = useState(false)
  const [isEmailDropdownOpen, setIsEmailDropdownOpen] = useState(false)
  const inputClass = readOnly ? 'bg-muted cursor-not-allowed' : ''

  useEffect(() => {
    if (readOnly || contactUserId == null) {
      setIsEmailEditing(false)
    }
    if (contactUserId == null) {
      setSelectedContactInfo(null)
    } else if (group.contactUserId != null && group.contactUserId === contactUserId) {
      setSelectedContactInfo({
        email: group.contact_email ?? undefined,
        phone: group.contact_phone ?? undefined,
      })
    }
  }, [contactUserId, group.contactUserId, group.contact_email, group.contact_phone, readOnly])

  useEffect(() => {
    if (contactUserId != null) {
      setIsNoGroupLeaderCleared(false)
      return
    }
    if ((newContact.name ?? '').trim().length > 0) {
      setIsNoGroupLeaderCleared(false)
    }
  }, [contactUserId, newContact.name])

  const displayEmail = getDisplayEmailValue(selectedContactInfo?.email ?? contactEmail)
  const displayPhone = selectedContactInfo?.phone ?? contactPhone
  const groupLeaderDisplayValue =
    contactUserId == null
      ? (
          newContact.name.trim() ||
          (isNoGroupLeaderCleared ? '' : (group.contactUserId == null ? 'No Group Leader' : ''))
        )
      : (group.contact_name ?? undefined)

  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2 sm:col-span-2">
          <div className="flex items-center gap-1.5">
            <Label htmlFor="group-leader-contact">Group Leader</Label>
            <Popover open={groupLeaderInfoOpen} onOpenChange={setGroupLeaderInfoOpen}>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label="Group Leader info"
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                    <p>{GROUP_LEADER_INFO}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <PopoverContent side="bottom" className="max-w-[280px] text-sm">
                <p>{GROUP_LEADER_INFO}</p>
              </PopoverContent>
            </Popover>
          </div>
          <ContactUserSelect
            value={contactUserId}
            onChange={(userId) => onContactUserIdChange(userId)}
            onVolunteerSelect={(v) => {
              setSelectedContactInfo({ email: v.email, phone: v.phone })
              onSelectedContactInfoChange({ userId: v.id, email: v.email, phone: v.phone })
            }}
            onDisplayValueChange={(name) => {
              const isNoGroupLeaderSelection = name.trim().toLowerCase() === 'no group leader'
              if (isNoGroupLeaderSelection) {
                onContactUserIdChange(null)
                onNewContactChange((prev) => ({
                  ...prev,
                  name: '',
                  email: NO_EMAIL_AVAILABLE_OPTION,
                  phone: '',
                }))
                setIsNoGroupLeaderCleared(false)
                return
              }
              if (contactUserId != null) {
                onContactUserIdChange(null)
                onNewContactChange((prev) => ({ ...prev, name, email: '', phone: '' }))
              } else {
                onNewContactChange((prev) => ({
                  ...prev,
                  name,
                  // If user begins entering a real leader name, clear no-email placeholder.
                  email:
                    name.trim().length > 0 && isNoEmailPlaceholderValue(prev.email)
                      ? ''
                      : prev.email,
                }))
              }
              setIsNoGroupLeaderCleared(name.trim().length === 0)
            }}
            displayValue={groupLeaderDisplayValue}
            showDisplayValueWhenNoValue
            placeholder="Select Group Leader"
            noSelectionOptionLabel="No Group Leader"
            disabled={readOnly}
          />
        </div>
        {contactUserId != null ? (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Label htmlFor="group-leader-contact-email">Group Leader Email *</Label>
                {!readOnly && (
                  !isEmailEditing ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        setEmailBeforeEdit(selectedContactEmail)
                        setIsEmailEditing(true)
                      }}
                    >
                      Edit email
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        onSelectedContactEmailChange(emailBeforeEdit)
                        setIsEmailEditing(false)
                      }}
                    >
                      Cancel email edit
                    </Button>
                  )
                )}
              </div>
              {!readOnly && isEmailEditing ? (
                <Popover open={isEmailDropdownOpen} onOpenChange={setIsEmailDropdownOpen}>
                  <PopoverTrigger asChild>
                    <div className="relative">
                      <Input
                        id="group-leader-contact-email"
                        type="text"
                        value={selectedContactEmail}
                        onChange={(e) => onSelectedContactEmailChange((e.target as HTMLInputElement).value)}
                        className={cn(inputClass, 'pr-10')}
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                        onClick={() => setIsEmailDropdownOpen((v) => !v)}
                        aria-label="Toggle email options"
                      >
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                    <div className="p-1">
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          onSelectedContactEmailChange(NO_EMAIL_AVAILABLE_OPTION)
                          setIsEmailDropdownOpen(false)
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                          "hover:bg-accent hover:text-accent-foreground",
                          isNoEmailPlaceholderValue(selectedContactEmail) && "bg-accent"
                        )}
                      >
                        <Check className={cn("h-4 w-4 shrink-0", isNoEmailPlaceholderValue(selectedContactEmail) ? "opacity-100 text-primary" : "opacity-0")} />
                        <span className="truncate">{NO_EMAIL_AVAILABLE_OPTION}</span>
                      </button>
                      <div className="py-2 text-center text-sm text-muted-foreground">Type to search by email.</div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <Input
                  id="group-leader-contact-email"
                  type="text"
                  value={displayEmail}
                  disabled={readOnly || !isEmailEditing}
                  readOnly={readOnly}
                  className={readOnly ? 'bg-muted cursor-not-allowed' : inputClass}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-leader-contact-phone">Group Leader Phone</Label>
              <Input
                id="group-leader-contact-phone"
                type="text"
                value={readOnly ? displayPhone : selectedContactPhone}
                onChange={(e) => onSelectedContactPhoneChange((e.target as HTMLInputElement).value)}
                disabled={readOnly}
                readOnly={readOnly}
                className={readOnly ? 'bg-muted cursor-not-allowed' : inputClass}
              />
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Label htmlFor="group-leader-contact-email">Group Leader Email *</Label>
              </div>
              <Popover open={isEmailDropdownOpen && !readOnly} onOpenChange={setIsEmailDropdownOpen}>
                <PopoverTrigger asChild>
                  <div className="relative">
                    <Input
                      id="group-leader-contact-email"
                      type="text"
                      placeholder="email@example.com"
                      value={newContact.email}
                      onChange={(e) => onNewContactChange((prev) => ({ ...prev, email: (e.target as HTMLInputElement).value }))}
                      disabled={readOnly}
                      readOnly={readOnly}
                      className={cn(inputClass, !readOnly && 'pr-10')}
                    />
                    {!readOnly && (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                        onClick={() => setIsEmailDropdownOpen((v) => !v)}
                        aria-label="Toggle email options"
                      >
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <div className="p-1">
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        onNewContactChange((prev) => ({ ...prev, email: NO_EMAIL_AVAILABLE_OPTION }))
                        setIsEmailDropdownOpen(false)
                      }}
                      className={cn(
                        "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        isNoEmailPlaceholderValue(newContact.email) && "bg-accent"
                      )}
                    >
                      <Check className={cn("h-4 w-4 shrink-0", isNoEmailPlaceholderValue(newContact.email) ? "opacity-100 text-primary" : "opacity-0")} />
                      <span className="truncate">{NO_EMAIL_AVAILABLE_OPTION}</span>
                    </button>
                    <div className="py-2 text-center text-sm text-muted-foreground">Type to search by email.</div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-leader-contact-phone">Group Leader Phone</Label>
              <Input
                id="group-leader-contact-phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={newContact.phone}
                onChange={(e) => onNewContactChange((prev) => ({ ...prev, phone: (e.target as HTMLInputElement).value }))}
                disabled={readOnly}
                readOnly={readOnly}
                className={inputClass}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
